import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext.tsx';
import { ModuleProvider, useModules } from './context/ModuleContext.tsx';
import { Navbar } from './components/layout/Navbar.tsx';
import { Sidebar } from './components/layout/Sidebar.tsx';
import { GlobalSearchModal } from './components/common/GlobalSearchModal.tsx';
import { ErrorBoundary } from './components/common/ErrorBoundary.tsx';

// Pages
import { DashboardPage } from './pages/DashboardPage.tsx';
import { ModuleManagerPage } from './pages/ModuleManagerPage.tsx';
import { BranchesPage } from './pages/BranchesPage.tsx';
import { TicketsPage } from './pages/TicketsPage.tsx';
import { ProjectsPage } from './pages/ProjectsPage.tsx';
import { ProjectDetailPage } from './pages/ProjectDetailPage.tsx';
import { SiteSurveyPage } from './pages/SiteSurveyPage.tsx';
import { BOQPage } from './pages/BOQPage.tsx';
import { QuotationsPage } from './pages/QuotationsPage.tsx';
import { PurchasePage } from './pages/PurchasePage.tsx';
import { InventoryPage } from './pages/InventoryPage.tsx';
import { SiteWorkPage } from './pages/SiteWorkPage.tsx';
import { CompletionPage } from './pages/CompletionPage.tsx';
import { InvoicesPage } from './pages/InvoicesPage.tsx';
import { ConsolidatedInvoicesPage } from './pages/ConsolidatedInvoicesPage.tsx';
import { AccountsPage } from './pages/AccountsPage.tsx';
import { ExpensesPage } from './pages/ExpensesPage.tsx';
import { ProjectCostingPage } from './pages/ProjectCostingPage.tsx';
import { DocumentsPage } from './pages/DocumentsPage.tsx';
import { AuditLogPage } from './pages/AuditLogPage.tsx';
import { ReportsPage } from './pages/ReportsPage.tsx';

// HERE4U Operational Pages
import { Here4uCenterPage } from './pages/Here4uCenterPage.tsx';
import { GmailCenterPage } from './pages/GmailCenterPage.tsx';
import { TemporaryCasesPage } from './pages/TemporaryCasesPage.tsx';
import { StaffTeamsPage } from './pages/StaffTeamsPage.tsx';
import { WorkOrdersPage } from './pages/WorkOrdersPage.tsx';
import { FieldAttendancePage } from './pages/FieldAttendancePage.tsx';
import { JobVerificationPage } from './pages/JobVerificationPage.tsx';
import { WorkEvidencePage } from './pages/WorkEvidencePage.tsx';
import { EstimatorTemplatePage } from './pages/EstimatorTemplatePage.tsx';
import { MobileJobPage } from './pages/MobileJobPage.tsx';

const AppContent: React.FC = () => {
  const [currentRoute, setCurrentRoute] = useState<string>('dashboard');
  const [routeParam, setRouteParam] = useState<string>('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [mobileMode, setMobileMode] = useState(false);

  const { isModuleActive } = useModules();

  const handleNavigate = (route: string, param?: string) => {
    setCurrentRoute(route);
    if (param) setRouteParam(param);
    setIsSidebarOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Keyboard shortcut for Global Search (Ctrl+K or Cmd+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const renderCurrentPage = () => {
    switch (currentRoute) {
      case 'dashboard':
        return <DashboardPage onNavigate={handleNavigate} mobileMode={mobileMode} />;
      case 'modules':
        return <ModuleManagerPage />;
      case 'branches':
        return <BranchesPage onNavigate={handleNavigate} />;
      case 'tickets':
        return <TicketsPage onNavigate={handleNavigate} />;
      case 'projects':
        return <ProjectsPage onNavigate={handleNavigate} />;
      case 'project-detail':
        return (
          <ProjectDetailPage
            projectId={routeParam || '1'}
            onBack={() => handleNavigate('projects')}
            onNavigate={handleNavigate}
          />
        );
      case 'surveys':
        return <SiteSurveyPage onNavigate={handleNavigate} />;
      case 'boqs':
        return <BOQPage onNavigate={handleNavigate} />;
      case 'quotations':
        return <QuotationsPage onNavigate={handleNavigate} />;
      case 'purchase':
        return <PurchasePage onNavigate={handleNavigate} />;
      case 'inventory':
        return <InventoryPage onNavigate={handleNavigate} />;
      case 'sitework':
        return <SiteWorkPage onNavigate={handleNavigate} />;
      case 'completion':
        return <CompletionPage onNavigate={handleNavigate} />;
      case 'invoices':
        return <InvoicesPage onNavigate={handleNavigate} />;
      case 'consolidated-invoices':
        return <ConsolidatedInvoicesPage onNavigate={handleNavigate} />;
      case 'accounts':
        return <AccountsPage onNavigate={handleNavigate} />;
      case 'expenses':
        return <ExpensesPage onNavigate={handleNavigate} />;
      case 'costing':
        return <ProjectCostingPage onNavigate={handleNavigate} />;
      case 'documents':
        return <DocumentsPage onNavigate={handleNavigate} />;
      case 'reports':
        return <ReportsPage />;
      case 'audit':
        return <AuditLogPage />;
      case 'here4u':
        return <Here4uCenterPage onNavigate={handleNavigate} />;
      case 'mobile-job':
        return <MobileJobPage onNavigate={handleNavigate} staffIdParam={routeParam} />;
      case 'gmail':
        return <GmailCenterPage onNavigate={handleNavigate} />;
      case 'temp-cases':
        return <TemporaryCasesPage onNavigate={handleNavigate} />;
      case 'staff':
        return <StaffTeamsPage onNavigate={handleNavigate} />;
      case 'work-orders':
        return <WorkOrdersPage onNavigate={handleNavigate} />;
      case 'attendance':
        return <FieldAttendancePage onNavigate={handleNavigate} />;
      case 'verification':
        return <JobVerificationPage onNavigate={handleNavigate} />;
      case 'evidence':
        return <WorkEvidencePage onNavigate={handleNavigate} />;
      case 'estimator-templates':
        return <EstimatorTemplatePage onNavigate={handleNavigate} />;
      default:
        return <DashboardPage onNavigate={handleNavigate} mobileMode={mobileMode} />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-800 antialiased flex flex-col">
      {/* Top Navbar */}
      <Navbar
        onToggleSidebar={() => setIsSidebarOpen((prev) => !prev)}
        onOpenSearch={() => setIsSearchOpen(true)}
        onNavigate={handleNavigate}
        mobileViewMode={mobileMode}
        onToggleMobileMode={() => setMobileMode((prev) => !prev)}
      />

      {/* Main Layout Body */}
      <div className="flex flex-1 w-full min-w-0">
        {/* Left Dark Blue Sidebar */}
        <Sidebar
          currentRoute={currentRoute}
          onNavigate={handleNavigate}
          isOpen={isSidebarOpen}
          onCloseMobile={() => setIsSidebarOpen(false)}
        />

        {/* Page Content Container with ErrorBoundary - Full width desktop layout starting strictly after sidebar */}
        <main className="app-main-area p-4 sm:p-6 lg:p-6 xl:p-8 2xl:px-9 min-w-0">
          <div className="w-full max-w-none m-0 min-w-0">
            <ErrorBoundary fallbackTitle="View Rendering Error">
              {renderCurrentPage()}
            </ErrorBoundary>
          </div>
        </main>
      </div>

      {/* Global Search Modal */}
      <GlobalSearchModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        onNavigate={(path) => {
          const cleanRoute = path.replace('/', '');
          handleNavigate(cleanRoute);
        }}
      />
    </div>
  );
};

export default function App() {
  return (
    <ErrorBoundary fallbackTitle="Naeem Builder System Error">
      <AuthProvider>
        <ModuleProvider>
          <AppContent />
        </ModuleProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}
